import { Box, Button, Grid } from '@material-ui/core';
import { observer } from 'mobx-react';
import React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { GetActiveContractsResponseDto } from '../../../core/api/dtos';
import { getSitekey } from '../../../core/common/recaptcha';
import { injectTSDI } from '../../../core/common/tsdi';
import { I18n } from '../../../core/i18n';
import { AdditionalInfoText } from '../../components/inputs/additional-info-text';
import { ReasonSelect } from '../../components/inputs/reason-select';
import { TypeSelect } from '../../components/inputs/type-select';
import { SharedContractDissolutionStore } from '../../shared-contract-dissolution-store';
import { BottomPaddedGridItemS, StyledReCAPTCHA } from '../../styles';
import { ContractTerminationStore } from '../contract-termination-store';
import { ConfirmationEmailInput } from '../inputs/confirmation-email-input';
import { DatePicker } from '../inputs/date-picker';
import { DateSelect } from '../inputs/date-select';

export interface ContractTerminationFormProps {
    contract: GetActiveContractsResponseDto;
}

export const ContractTerminationForm = observer(
    (props: ContractTerminationFormProps) => {
        const recaptchaRef = React.createRef<ReCAPTCHA>();
        const { translate } = injectTSDI(I18n);
        const contractTerminationStore = injectTSDI(ContractTerminationStore);
        const sharedStore = injectTSDI(SharedContractDissolutionStore);

        const cancelContractForm =
            contractTerminationStore.getCancelContractForm(
                props.contract.contractId
            );

        if (!cancelContractForm || !props.contract.contractId) {
            return <></>;
        }

        const { contractId } = props.contract;

        const {
            confirmationEmail,
            cancelationReasonId,
            cancellationType,
            additionalInformation
        } = cancelContractForm.wrappedField;

        const { updateCancellationType, extraordinaryCancellationHelperText } =
            sharedStore;

        const cancellationDateInputElement =
            cancellationType.value === 'EXTRAORDINARY_CANCELLATION' ? (
                <DatePicker contract={props.contract} minDate="TODAY" />
            ) : props.contract.cancellationType === 'FIXED_DATES' ? (
                <DateSelect contract={props.contract} />
            ) : (
                <DatePicker
                    contract={props.contract}
                    minDate="EARLIEST_FOR_CONTRACT"
                />
            );

        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <TypeSelect
                        cancellationTypeField={cancellationType}
                        onChange={(event) =>
                            updateCancellationType(event, cancellationType)
                        }
                        helperTextExtraordinaryTermination={
                            extraordinaryCancellationHelperText
                        }
                        data-role="contract-termination-type"
                    />
                </Grid>
                <Box width="100%" />
                <BottomPaddedGridItemS item xs={12} sm={6}>
                    {cancellationDateInputElement}
                </BottomPaddedGridItemS>
                <Grid item xs={12} sm={6}>
                    <ReasonSelect
                        cancellationTypeField={cancellationType}
                        cancellationReasonIdField={cancelationReasonId}
                        studioId={props.contract.studioId}
                    />
                </Grid>
                {cancellationType.value === 'EXTRAORDINARY_CANCELLATION' ? (
                    <Grid item xs={12}>
                        <AdditionalInfoText
                            additionalInformationField={additionalInformation}
                            helperText={translate(
                                'public.content.terminate.contract.info.extraordinary'
                            )}
                            data-role="contract-termination-additional-info"
                        />
                    </Grid>
                ) : undefined}
                <BottomPaddedGridItemS item xs={12} sm={6}>
                    <ConfirmationEmailInput
                        confirmationEmailField={confirmationEmail}
                        data-role="confirmation-email"
                    />
                </BottomPaddedGridItemS>
                <Grid item xs={12}>
                    <StyledReCAPTCHA
                        sitekey={getSitekey()}
                        ref={recaptchaRef}
                        size="invisible"
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        data-role="submit-terminate-contract"
                        onClick={() =>
                            contractTerminationStore.handleTerminate(
                                recaptchaRef,
                                contractId
                            )
                        }
                    >
                        {translate('public.content.terminate.contract.now')}
                    </Button>
                </Grid>
            </Grid>
        );
    }
);
